import { BuyerSearchResult } from "@/types/buyerSearch";
import { BuyerCard } from "./BuyerCard";
import { BuyerSearchListViewRow } from "./BuyerSearchListViewRow";
import { BuyerViewToggle } from "@/components/seller/BuyerViewToggle";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Download } from "lucide-react";

interface BuyerSearchResultsProps {
  buyers: BuyerSearchResult[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onAddToList: (buyerId: string) => void;
  onContact: (buyerId: string) => void;
  onExport?: () => void;
  viewMode: 'list' | 'tile';
  onViewModeChange: (mode: 'list' | 'tile') => void;
}

export const BuyerSearchResults = ({
  buyers,
  totalCount,
  currentPage,
  totalPages,
  onPageChange,
  onAddToList,
  onContact,
  onExport,
  viewMode,
  onViewModeChange
}: BuyerSearchResultsProps) => {
  const startIndex = (currentPage - 1) * 20 + 1;
  const endIndex = Math.min(currentPage * 20, totalCount);

  if (buyers.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No buyers found</h3>
          <p className="text-muted-foreground text-center">
            Try adjusting your search filters to find more buyers.
          </p>
        </CardContent>
      </Card>
    );
  }

  const generatePaginationLinks = () => {
    const links = [];
    const showRange = 2;
    
    let startPage = Math.max(1, currentPage - showRange);
    let endPage = Math.min(totalPages, currentPage + showRange);

    if (endPage - startPage < showRange * 2) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + showRange * 2);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - showRange * 2);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      links.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => onPageChange(i)}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return links;
  };

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span className="font-medium text-sm sm:text-base">
                  {totalCount} buyers found
                </span>
              </div>
              {totalCount > 0 && (
                <Badge variant="secondary" className="text-xs w-fit">
                  Showing {startIndex}-{endIndex}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <BuyerViewToggle value={viewMode} onChange={onViewModeChange} />
              {onExport && totalCount > 0 && (
                <Button variant="outline" size="sm" onClick={onExport} className="text-xs sm:text-sm">
                  <Download className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results - Tile View */}
      {viewMode === 'tile' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {buyers.map((buyer, index) => (
            <BuyerCard
              key={buyer.id}
              buyer={buyer}
              onAddToList={onAddToList}
              onContact={onContact}
              isFirstCard={index === 0}
            />
          ))}
        </div>
      )}

      {/* Results - List View */}
      {viewMode === 'list' && (
        <Card>
          <div className="divide-y">
            {buyers.map((buyer) => (
              <BuyerSearchListViewRow
                key={buyer.id}
                buyer={buyer}
                onAddToList={onAddToList}
                onContact={onContact}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => onPageChange(currentPage - 1)}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {generatePaginationLinks()}
                
                <PaginationItem>
                  <PaginationNext
                    onClick={() => onPageChange(currentPage + 1)}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
